import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/ui/form-error";
import { UPDATE_PROJECT } from "../../../../GraphQL/settings/mutations";
import { EToastColorStatus, EToastPosition, IProject } from "../../../../interfaces";

const editProjectSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string(),
});

interface IEditProjectProps {
  showEditModal: boolean;
  onCloseModal: () => void;
  projectId: number | null;
  projectList: IProject[] | [];
  reloadProjects: () => void;
  onHandleCreateToast: (
    color    : EToastColorStatus,
    position : EToastPosition,
    message  : string,
  ) => void;
}

const EditProjectModal = ({
  showEditModal,
  onCloseModal,
  projectId,
  projectList,
  reloadProjects,
  onHandleCreateToast = () => {},
}: IEditProjectProps) => {
  const [singleProject, setSingleProject] = useState<IProject | undefined>(undefined);
  const [updateProject] = useMutation(UPDATE_PROJECT, {
    onCompleted() {
      reloadProjects();
      onHandleCreateToast(
        EToastColorStatus.SUCCESS,
        EToastPosition.BOTTOM_END,
        "Project successfully updated",
      );
    },
    onError() {
      onHandleCreateToast(
        EToastColorStatus.FAILED,
        EToastPosition.BOTTOM_END,
        "Error, something went wrong",
      );
    },
  });

  useEffect(() => {
    if (projectList) {
      setSingleProject(projectList.find((project: IProject) => project.id === projectId));
    }
  }, [projectList, projectId]);

  const formik = useFormik({
    initialValues: {
      title: singleProject?.title ?? "",
      description: singleProject?.description ?? "",
    },
    validationSchema: editProjectSchema,
    onSubmit: (values) => {
      updateProject({
        variables: {
          id: projectId,
          title: values?.title,
          description: values?.description,
        },
      });
      onCloseModal();
    },
    enableReinitialize: true,
  });

  const { errors, touched } = formik;

  return (
    <Dialog open={showEditModal} onOpenChange={onCloseModal}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit} className="space-y-lg">
          <div className="space-y-sm">
            <Label htmlFor="title" className="text-base font-medium">
              Title
            </Label>
            <Input
              id="title"
              name="title"
              type="text"
              placeholder="Enter project title"
              onChange={formik.handleChange}
              value={formik?.values?.title}
            />
            <FormError>
              {errors?.title && touched?.title ? errors?.title as string : null}
            </FormError>
          </div>

          <div className="space-y-sm">
            <Label htmlFor="description" className="text-base font-medium">
              Description
            </Label>
            <Textarea
              id="description"
              name="description"
              rows={3}
              placeholder="Enter project description"
              onChange={formik.handleChange}
              value={formik?.values?.description}
            />
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={onCloseModal}>
            Cancel
          </Button>
          <Button variant="default" onClick={formik.submitForm}>
            Update Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditProjectModal;
